import LanguageLocaleUtils from 'utils/LanguageLocale';
import PasskeysActionsEN from 'actions/locales/PasskeysActions_en_US';
import PasskeysActionsFR from 'actions/locales/PasskeysActions_fr_CA';
import { ERR_PK_LOGIN_ERROR } from 'constants/passkeys';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(PasskeysActionsEN, PasskeysActionsFR);

/**
 * SIM-24 (ACS-4418) LEGACY-PARITY error surface for every passkey sign-in failure that reaches
 * a caller of this module. Matches the legacy `handlePasskeyError` behavior in
 * services/api/authentication/login.js verbatim — same modal (title + copy + button), same
 * Adobe Analytics call (BeyondIdentityUtils.trackPasskeyError → prop28='passkey:{reason}' +
 * prop48='{errorId} | {message}'), same navigation-deferred-to-modal-close ordering.
 *
 * The old sign-in modal flow fired both signals for EVERY passkey error (user cancel included,
 * biometric timeout included, backend 5xx included, pre-WebAuthn API failure included) by
 * routing the error through login.js's `handlePasskeyError` when suppressErrorModal was falsy.
 * The new SIM-24 dispatch sites pass suppressErrorModal:true so login.js does NOT show its
 * own modal (would reopen the legacy SignInModal on close, which we don't want in the new
 * flow) — this helper replicates the modal + analytics parts of handlePasskeyError while
 * letting the caller supply its own navigation via `onDismiss` (fired on OK / dismiss).
 *
 * NO gating on operation / category — every rejection reaching this helper fires modal +
 * analytics. Caller's onDismiss runs ONLY after the user acknowledges, so navigation never
 * clips the modal.
 *
 * SIM-24 (ACS-4418) F-012 completion: the returned Promise SETTLES INSIDE THE MODAL'S OK
 * CALLBACK (not right after the store.dispatch line). Awaiting callers therefore hold their
 * in-flight state / lock through the full modal lifetime — mirrors CR-4's contract for
 * openTriggerPasskeySelector. Without this, awaiting `.then(...)` would fire before the
 * modal even renders, and the F-012 `inFlightNavigate` guard clears while the failure modal
 * is still visible → concurrent navigateToSignIn calls (background 401 defers, second
 * requireAuthentication check, stale showSignInModal re-route) re-preflight and re-fire
 * WebAuthn behind the still-open modal.
 *
 * @param {*} error - The rejection value from the passkey sign-in dispatch. Expected to carry
 *   the login.js-attached fields `reason` (for prop28) and `errorCode` (for prop48 + copy
 *   selection). Errors that bypass login.js (e.g. synchronous store.dispatch throw) still
 *   fire modal + analytics but with `reason=null` → prop28='passkey:untracked'.
 * @param {Function} [onDismiss] - Fired EXACTLY ONCE after the user clicks OK. Use this to
 *   navigate/redirect AFTER the modal is dismissed so the message is not clipped by a page
 *   transition. Also fired on defensive .catch of this helper's own Promise (see callers).
 * @returns {Promise<boolean>} Resolves true AFTER the user dismisses the modal (OK click).
 *   Callers should `await` to hold in-flight state / lock through the modal lifetime.
 *   Rejects only on a hard dynamic-import failure of store/Store, Actions, or lazyPasskey —
 *   which callers guard with their own `.catch` so onDismiss still fires.
 */
export const showPasskeyFailureModalIfNeeded = async (error, onDismiss) => {
    const invokeDismiss = () => {
        if (typeof onDismiss === 'function') {
            onDismiss();
        }
    };

    const [{ default: store }, { default: Actions }, { loadBeyondIdentityUtils }] = await Promise.all([
        import('store/Store'),
        import('Actions'),
        import('utils/lazyPasskey')
    ]);

    // Copy selection matches legacy login.js handlePasskeyError:59 — preserved backend
    // errorCode (via Fix 1 at Site 1) chooses passkeyLoginError; anything else falls to the
    // generic passkeyAuthenticationErrorMessage ("An error occurred while signing you in.
    // Please use your email and password to sign in.").
    const errorMessage = error?.errorCode === ERR_PK_LOGIN_ERROR ? getText('passkeyLoginError') : getText('passkeyAuthenticationErrorMessage');

    // Adobe Analytics — matches legacy handlePasskeyError → BeyondIdentityUtils.trackPasskeyError.
    // reason is the login.js-attached string (Fix: 'api:login' / 'api:authenticate' / 'sdk:authenticate'
    // / 'biometric' / etc.) — trackPasskeyError prefixes it with 'passkey:' to build prop28.
    // Wrapped in try/catch so an analytics failure (SDK-load reject, network) NEVER blocks the
    // user-facing modal. Legacy would silently fail here too (no try/catch in handlePasskeyError)
    // but its failure mode was harsher — we're stricter defensive.
    try {
        const BeyondIdentityUtils = await loadBeyondIdentityUtils();
        BeyondIdentityUtils.trackPasskeyError(errorMessage, error, error?.reason || null);
    } catch (trackErr) {
        // Best-effort analytics — swallow and continue to the modal dispatch.
    }

    // F-012 completion: wrap the dispatch in a Promise that resolves inside the modal's OK
    // callback, not after store.dispatch returns. Callers await this to hold in-flight state
    // (F-012 inFlightNavigate guard at trigger site; passkeySignInLock elsewhere) through the
    // modal's full lifetime. Ordering inside the callback: close info modal → invokeDismiss
    // (fires caller's navigation) → resolve. Resolve fires after invokeDismiss so any
    // caller-side finally that clears in-flight state runs AFTER the user's navigation has
    // been dispatched — matches openTriggerPasskeySelector's CR-4 resolve-after-side-effect
    // ordering exactly.
    return new Promise(resolve => {
        store.dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('error'),
                message: errorMessage,
                buttonText: getText('ok'),
                showCloseButton: false,
                callback: () => {
                    store.dispatch(Actions.showInfoModal({ isOpen: false }));
                    try {
                        invokeDismiss();
                    } finally {
                        resolve(true);
                    }
                }
            })
        );
    });
};
